import type { Language } from '@/i18n';

type NonEmptyStrings = readonly [string, ...string[]];

export interface LandingPricingLadderCopy {
  ownerHeading: string;
  transactionsHeading: string;
  checkoutUnavailable: string;
  free: {
    name: string;
    benefit: string;
    scope: string;
    boundary: string;
    includes: NonEmptyStrings;
    action: string;
  };
  owner: {
    benefit: string;
    inheritance: string;
    includes: NonEmptyStrings;
  };
  portfolio: {
    benefit: string;
    inheritance: string;
    includes: NonEmptyStrings;
  };
}

const COPY = {
  fi: {
    ownerHeading: 'Metsänomistajalle',
    transactionsHeading: 'Apua kaupan hetkellä',
    checkoutUnavailable: 'Ostaminen ei ole tällä hetkellä käytössä.',
    free: {
      name: 'Maksuton',
      benefit: 'Näe, mitä metsästäsi tiedetään nyt.',
      scope: 'Yhden metsätilan perustiedot',
      boundary:
        'Ei sisällä Lehti AI:n laskemaa ja tallennettavaa suunnitelmaa eikä omien mittausten lisäämistä.',
      includes: [
        'Kartta, kuviot ja metsän perustiedot lähteineen',
        'Metsäkeskuksen toimenpidesuositusten tarkastelu',
        'Alueelliset tukki- ja kuitupuun hinnat sekä hintahistoria',
      ],
      action: 'Lisää metsätila',
    },
    owner: {
      benefit: 'Tee suunnitelma ja pidä se ajan tasalla.',
      inheritance: 'Kaikki maksuttoman version ominaisuudet, lisäksi:',
      includes: [
        'Omat mittaustiedot: käsin, sovelluksesta tai tiedostona',
        'Kasvun ja tulojen laskenta sekä suunnitelmavaihtoehtojen vertailu',
        'Oman suunnitelman tallennus ja muokkaus tekoälyn avulla',
        'Metsän hinta-arvio ja puukaupan ajoituksen arviointi',
        'Metsän muutosten jatkuva seuranta ja ilmoitukset',
        'PDF-raportit ja tarjouspyyntöjen vienti',
      ],
    },
    portfolio: {
      benefit: 'Hallitse metsätilojasi yhdessä paikassa.',
      inheritance: 'Kaikki Tonttipaketin ominaisuudet usealle metsätilalle, lisäksi:',
      includes: ['Metsiesi yhteinen tilannekuva ja seuranta', 'Tietojen vienti Exceliin'],
    },
  },
  en: {
    ownerHeading: 'Choose how you stay on top of your forest',
    transactionsHeading: 'When a purchase or offer is on the table',
    checkoutUnavailable: 'Purchases are currently disabled.',
    free: {
      name: 'Free',
      benefit: 'See what is known about your forest today.',
      scope: 'Essential facts for one forest property',
      boundary:
        'Does not include a calculated, saved Lehti AI plan or adding your own measurements.',
      includes: [
        'Map, stands and essential forest facts with sources',
        'View Finnish Forest Centre operation recommendations',
        'Regional sawlog and pulpwood prices and price history',
      ],
      action: 'Add a forest property',
    },
    owner: {
      benefit: 'Make a plan and keep it current.',
      inheritance: 'Everything in Free, plus:',
      includes: [
        'Add your own measurements manually, from an app or a file',
        'Calculate growth and income and compare alternative plans',
        'Save and edit your plan with AI assistance',
        'Forest valuation and timber-sale timing analysis',
        'Continuous forest-change monitoring and notifications',
        'PDF reports and tender-request exports',
      ],
    },
    portfolio: {
      benefit: 'Manage all your forests from one view.',
      inheritance: 'Everything in Plot Package across multiple properties, plus:',
      includes: ['A shared overview and monitoring across your forests', 'Export data to Excel'],
    },
  },
  sv: {
    ownerHeading: 'Välj hur du håller greppet om din skog',
    transactionsHeading: 'När ett köp eller anbud står på spel',
    checkoutUnavailable: 'Köp är inte tillgängliga just nu.',
    free: {
      name: 'Kostnadsfri',
      benefit: 'Se vad som är känt om din skog i dag.',
      scope: 'Grundläggande fakta för en skogsfastighet',
      boundary:
        'Omfattar inte en beräknad och sparad Lehti AI-plan eller tillägg av egna mätningar.',
      includes: [
        'Karta, bestånd och grundläggande skogsdata med källor',
        'Se Skogscentralens åtgärdsrekommendationer',
        'Regionala timmer- och massavedspriser samt prishistorik',
      ],
      action: 'Lägg till en skogsfastighet',
    },
    owner: {
      benefit: 'Gör en plan och håll den aktuell.',
      inheritance: 'Allt i den kostnadsfria versionen, plus:',
      includes: [
        'Lägg till egna mätningar manuellt, från en app eller en fil',
        'Beräkna tillväxt och intäkter och jämför alternativa planer',
        'Spara och redigera din plan med AI-hjälp',
        'Skogsvärdering och analys av tidpunkten för virkesförsäljning',
        'Löpande bevakning av skogsförändringar och aviseringar',
        'PDF-rapporter och export av anbudsförfrågningar',
      ],
    },
    portfolio: {
      benefit: 'Hantera alla dina skogar i en och samma vy.',
      inheritance: 'Allt i Skiftpaketet för flera skogsfastigheter, plus:',
      includes: ['Gemensam överblick och bevakning för dina skogar', 'Exportera data till Excel'],
    },
  },
  es: {
    ownerHeading: 'Elige cómo mantener el control de tu bosque',
    transactionsHeading: 'Cuando tienes una compra o una oferta delante',
    checkoutUnavailable: 'Las compras no están disponibles en este momento.',
    free: {
      name: 'Gratis',
      benefit: 'Descubre qué se sabe hoy de tu bosque.',
      scope: 'Datos esenciales de una finca forestal',
      boundary:
        'No incluye un plan calculado y guardado por Lehti AI ni la incorporación de mediciones propias.',
      includes: [
        'Mapa, rodales y datos forestales esenciales con sus fuentes',
        'Consulta las recomendaciones del Centro Forestal de Finlandia',
        'Precios regionales de madera de aserrío y para pasta, con su historial',
      ],
      action: 'Añadir una finca forestal',
    },
    owner: {
      benefit: 'Crea un plan y mantenlo al día.',
      inheritance: 'Todo lo del plan gratuito, más:',
      includes: [
        'Añade mediciones propias manualmente, desde una aplicación o un archivo',
        'Calcula el crecimiento y los ingresos y compara planes alternativos',
        'Guarda y modifica tu plan con ayuda de IA',
        'Valoración forestal y análisis del momento de vender madera',
        'Seguimiento continuo de cambios en el bosque y notificaciones',
        'Informes PDF y exportación de solicitudes de oferta',
      ],
    },
    portfolio: {
      benefit: 'Gestiona todos tus bosques desde una sola vista.',
      inheritance: 'Todo lo del paquete forestal para varias fincas, más:',
      includes: [
        'Vista conjunta y seguimiento de todos tus bosques',
        'Exportación de datos a Excel',
      ],
    },
  },
} satisfies Record<Language, LandingPricingLadderCopy>;

export function pricingLadderCopy(locale: Language): LandingPricingLadderCopy {
  return COPY[locale];
}
